import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { PlanDialogProps } from '../types';

export function PlanDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
}: PlanDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="PREMIUM_1M"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Premium 1 Month"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Duration Value *</Label>
              <Input
                type="number"
                value={formData.duration_value}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, duration_value: parseInt(e.target.value) || 1 }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Duration Unit *</Label>
              <Select
                value={formData.duration_unit}
                onValueChange={(value: 'week' | 'month' | 'year') => onFormDataChange(prev => ({ ...prev, duration_unit: value }))}
              >
                <SelectTrigger className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Currency</Label>
              <Input
                value={formData.currency}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, currency: e.target.value }))}
                placeholder="UZS"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Price *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Old Price</Label>
              <Input
                type="number"
                value={formData.old_price || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, old_price: e.target.value ? parseInt(e.target.value) : undefined }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => onFormDataChange(prev => ({ ...prev, is_active: checked }))}
            />
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(
              isDark 
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800' 
                : 'border-slate-200'
            )}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
